import { useAuth0User } from '@/app/modules/Context/useAuth0User';
import JobFavouriteButton from '@/app/modules/JobFavouriteButton';
import { getUtmShareUrl } from '@/app/utils/urlHelper';
import { JobSnippetStyle } from '@/cmsComponents/JobSnippet';
import { JobApplicationStatusLabel } from '@/components/JobApplicationStatusLabel';
import JobClientDetails from '@/components/JobClientDetails';
import { JobInformation } from '@/components/JobInformation';
import ShareContainer from '@/components/ShareContainer';
import { JobButton } from '@/modules/JobButton';
import { IJobDetail } from '@/types/jobs';
import { generateJobDetailsURL } from '@/utils/jobListingHelper';
import { useAnalytics } from '@/utils/useAnalytics';
import cx from 'classnames';
import Link from 'next/link';
import React from 'react';

import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';

import styles from './JobSnippetItem.module.scss';

interface IJobSnippetItemProps {
  data: IJobDetail;
  style: JobSnippetStyle;
}

const JobSnippetItem = ({ data, style }: IJobSnippetItemProps) => {
  const track = useAnalytics();

  const { id, title, isSaved, appliedStatus, grade, jobNumber, speciality, subspeciality } = data;
  const { user } = useAuth0User();
  const subspcialityOrSpeciality = subspeciality ? subspeciality : speciality;
  const jobDetailsUrl = generateJobDetailsURL(jobNumber, grade, subspcialityOrSpeciality);
  const jobSharingUrl = `${globalThis.location?.origin}${jobDetailsUrl}`;
  const newLineEncoding = '%0d%0a';
  const isMultiColumnLayout = [JobSnippetStyle.TwoColumns, JobSnippetStyle.ThreeColumns].includes(
    style,
  );
  const gridSize = isMultiColumnLayout ? 12 : 6;

  return (
    <article
      className={cx(styles.container, !isMultiColumnLayout && styles.containerBorder)}
      data-testid="job-snippet-item"
    >
      <Grid container alignItems="center">
        <Grid item xs={style === JobSnippetStyle.ThreeColumns ? 9 : 6}>
          <p data-testid="job-snippet-grade" className={styles.grade}>
            {grade}
          </p>
        </Grid>
        <Grid
          item
          container
          justifyContent="flex-end"
          xs={style === JobSnippetStyle.ThreeColumns ? 3 : 6}
        >
          {!appliedStatus && (
            <JobFavouriteButton
              id={id}
              jobNumber={jobNumber}
              favourited={isSaved}
              style={null}
              showText={style !== JobSnippetStyle.ThreeColumns}
            />
          )}
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={12} sm={gridSize}>
          <Link prefetch={false} href={jobDetailsUrl} as={jobDetailsUrl} passHref>
            {/* eslint-disable */}
            <a
              className={styles.title}
              onClick={() => {
                track('Job Details', {
                  'Details Origin': 'Job Card',
                  'Details Action': 'Title Link',
                  jobNumber: jobNumber,
                });
              }}
            >
              {/* eslint-enable */}
              <Typography variant="h5" component="h2" data-testid="job-snippet-speciality">
                {speciality}
              </Typography>
            </a>
          </Link>
          <JobInformation job={data} />
        </Grid>

        <Grid item container justifyContent="space-between" xs={12} sm={gridSize}>
          <JobClientDetails
            user={user}
            data={data}
            showDetails={style !== JobSnippetStyle.ThreeColumns}
          />

          <Grid
            item
            container
            justifyContent="space-between"
            alignItems="flex-end"
            className={styles.bottomContainer}
          >
            <Grid item>
              <ShareContainer
                jobOrArticleShare="jobshare"
                id={id}
                className={styles.jobShare}
                emailTitle={
                  <>
                    Who would you like <br /> to send this job to?
                  </>
                }
                emailSubject={`${user?.given_name ?? 'Medrecruit'} recommended a job for you`}
                emailBody={`View the job recommended for you by ${
                  user?.given_name ?? 'Medrecruit'
                } ${newLineEncoding}${encodeURI(
                  getUtmShareUrl('jobshare', jobSharingUrl, 'email'),
                )}`}
                facebookTitle={title}
                shareUrl={jobSharingUrl}
                shareTrackingProps={{
                  name: 'Share Job',
                  details: {
                    'Share Origin': 'Job Card',
                    jobNumber: jobNumber,
                  },
                }}
                popperPlacement={'bottom-start'}
              />
            </Grid>
            <Grid item className={styles.bottomButton}>
              {appliedStatus ? (
                <JobApplicationStatusLabel status={appliedStatus} />
              ) : (
                <JobButton
                  buttonSize="medium"
                  id={id}
                  jobNumber={jobNumber}
                  url={jobDetailsUrl}
                  text="See Details"
                  trackingCallback={() => {
                    track('Job Details', {
                      'Details Origin': 'Job Card Snippet',
                      'Details Action': 'Button',
                      jobNumber: jobNumber,
                    });
                  }}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </article>
  );
};

export default JobSnippetItem;
